import { useState, useEffect, useMemo, useRef } from "react";
import { format, subDays, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  FileSpreadsheet,
  Download,
  Filter,
  Search,
  RefreshCw,
  PackageCheck,
  ShoppingBag,
  DollarSign,
  AlertCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import { commercialCyclesApi, reportsApi } from "@/lib/api";
import {
  CYCLE_TYPE_LABELS,
  describeOrderType,
  resolveCycleYearFromCode,
} from "@/lib/orderCycle";
import {
  OrderItemsReportFilter,
  OrderItemsReportItem,
  PaymentMethodSource,
} from "@/types/reports";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
// Mesmo badge de status do pedido usado na Gestão de Pedidos do backoffice.
import StateBadge from "@/components/ui/stateBadge";
import { resolveDisplayOrderStatus } from "@/lib/orderStatus";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { toast } from "sonner";

const ALL = "ALL";
const COLUMN_COUNT = 11;

/** Período padrão do relatório: últimos 30 dias. */
const defaultDateRange = (): DateRange => ({ from: subDays(new Date(), 30), to: new Date() });

/** Se o período continua no padrão, "limpar filtros" não tem o que restaurar nele. */
function isDefaultDateRange(range: DateRange | undefined): boolean {
  if (!range?.from || !range?.to) return false;
  const padrao = defaultDateRange();
  return (
    format(range.from, "yyyy-MM-dd") === format(padrao.from!, "yyyy-MM-dd") &&
    format(range.to, "yyyy-MM-dd") === format(padrao.to!, "yyyy-MM-dd")
  );
}

/**
 * Tipo de pedido, enviado à API em `cycleType`: os 2 dígitos finais do `cycle_code`, mais "01S"
 * para o semestral (`PED-S-`), que sai no ciclo normal com o mesmo sufixo do anual.
 */
const CYCLE_TYPE_OPTIONS = [
  { value: "01", label: CYCLE_TYPE_LABELS["01"] },
  { value: "01S", label: "Semestral" },
  { value: "02", label: CYCLE_TYPE_LABELS["02"] },
  { value: "00", label: CYCLE_TYPE_LABELS["00"] },
];
const EXPORT_POLL_INTERVAL_MS = 2000;
const EXPORT_POLL_TIMEOUT_MS = 90000;

/** Mensagem de erro da API, com texto de apoio para os status que a tela trata de forma própria. */
function describeApiError(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
  const message = response?.data?.message;

  switch (response?.status) {
    case 400:
      return message || "Filtros inválidos. Revise o período e os critérios selecionados.";
    case 403:
      return "Você não tem permissão para acessar este relatório.";
    case 422:
      return message || "O volume do relatório é grande demais. Filtre por um período menor.";
    case 429:
      return message || "Já existe uma exportação em processamento. Aguarde a conclusão e tente novamente.";
    default:
      return message || fallback;
  }
}

export default function OrderItemsReport() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(defaultDateRange);

  const [cnpjInput, setCnpjInput] = useState("");
  const [cycleYearFilter, setCycleYearFilter] = useState<string>(ALL);
  const [cycleTypeFilter, setCycleTypeFilter] = useState<string>(ALL);
  const [situationFilter, setSituationFilter] = useState<string>(ALL);
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>(ALL);
  const [methodFilter, setMethodFilter] = useState<string>(ALL);
  const [brandFilter, setBrandFilter] = useState<string>(ALL);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [exporting, setExporting] = useState(false);

  // A API compara o CNPJ por igualdade: sem debounce, cada tecla dispararia uma consulta que
  // volta vazia até o 14º dígito.
  const cnpjFilter = useDebounce(cnpjInput.trim(), 500);

  // Vocabulários, catálogos e limites vêm da API: a tela não mantém cópia dessas listas.
  const { data: filterOptions } = useQuery({
    queryKey: ["order-items-report-filter-options"],
    queryFn: reportsApi.getFilterOptions,
    staleTime: 60 * 60 * 1000,
  });

  const maxPeriodDays = filterOptions?.maxPeriodDays;
  const periodDays =
    dateRange?.from && dateRange?.to
      ? differenceInCalendarDays(dateRange.to, dateRange.from)
      : 0;
  const hasPeriod = Boolean(dateRange?.from && dateRange?.to);
  // Enquanto as opções não chegam, não há limite conhecido para checar no front -- quem barra é a API.
  const periodTooLong = hasPeriod && maxPeriodDays != null && periodDays > maxPeriodDays;

  // O seletor de itens por página nunca oferece valor que a API recusaria.
  const pageSizeOptions = useMemo(
    () => [20, 50, 100].filter((size) => filterOptions == null || size <= filterOptions.maxPageSize),
    [filterOptions]
  );

  const filter: OrderItemsReportFilter | null = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    return {
      startDate: format(dateRange.from, "yyyy-MM-dd"),
      endDate: format(dateRange.to, "yyyy-MM-dd"),
      clientCnpj: cnpjFilter || undefined,
      // Ano e tipo de pedido são independentes na API: cada um filtra por conta.
      cycleYear: cycleYearFilter !== ALL ? parseInt(cycleYearFilter, 10) : undefined,
      cycleType: cycleTypeFilter !== ALL ? cycleTypeFilter : undefined,
      shippingSituation: situationFilter !== ALL ? situationFilter : undefined,
      orderStatus: orderStatusFilter !== ALL ? orderStatusFilter : undefined,
      paymentMethodCode: methodFilter !== ALL ? methodFilter : undefined,
      cardBrandCode: brandFilter !== ALL ? brandFilter : undefined,
    };
  }, [
    dateRange,
    cnpjFilter,
    cycleYearFilter,
    cycleTypeFilter,
    situationFilter,
    orderStatusFilter,
    methodFilter,
    brandFilter,
  ]);

  const hasActiveFilters =
    cnpjInput.trim() !== "" ||
    cycleYearFilter !== ALL ||
    cycleTypeFilter !== ALL ||
    situationFilter !== ALL ||
    orderStatusFilter !== ALL ||
    methodFilter !== ALL ||
    brandFilter !== ALL ||
    !isDefaultDateRange(dateRange);

  const handleClearFilters = () => {
    setDateRange(defaultDateRange());
    setCnpjInput("");
    setCycleYearFilter(ALL);
    setCycleTypeFilter(ALL);
    setSituationFilter(ALL);
    setOrderStatusFilter(ALL);
    setMethodFilter(ALL);
    setBrandFilter(ALL);
  };

  // Qualquer mudança de filtro volta para a primeira página: filtrar estando na página 5
  // consultava a página 5 do resultado novo.
  const filterSignature = JSON.stringify(filter);
  const previousSignature = useRef(filterSignature);
  useEffect(() => {
    if (previousSignature.current !== filterSignature) {
      previousSignature.current = filterSignature;
      setPageNumber(1);
    }
  }, [filterSignature]);

  const {
    data: reportData,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["order-items-report", filterSignature, pageNumber, pageSize],
    queryFn: () => reportsApi.getOrderItems({ ...filter!, pageNumber, pageSize }),
    enabled: Boolean(filter) && !periodTooLong,
    placeholderData: keepPreviousData,
  });

  const { data: cyclesResponse } = useQuery({
    queryKey: ["commercial-cycles"],
    queryFn: commercialCyclesApi.list,
  });

  // Anos de ciclo vindos da API, do mais recente para o mais antigo.
  const cycleYearOptions = useMemo(
    () =>
      Array.from(new Set((cyclesResponse?.data ?? []).map((cycle) => cycle.year))).sort(
        (a, b) => b - a
      ),
    [cyclesResponse]
  );

  useEffect(() => {
    if (periodTooLong) {
      toast.error(
        `O período selecionado (${periodDays} dias) excede o limite de ${maxPeriodDays} dias.`
      );
    }
  }, [periodTooLong, periodDays, maxPeriodDays]);

  const handleExport = async () => {
    if (!filter) {
      toast.warning("Selecione um período para exportar.");
      return;
    }
    if (periodTooLong) {
      toast.error(`Reduza o período para no máximo ${maxPeriodDays} dias antes de exportar.`);
      return;
    }

    try {
      setExporting(true);
      toast.info("Iniciando geração da planilha em segundo plano...");

      // Dispara o job assíncrono para o S3 (resposta instantânea)
      const job = await reportsApi.startExportJob(filter);

      toast.loading("Processando dados e enviando para o S3... O download iniciará em instantes.", {
        id: "export-job-toast",
      });

      pollExportJob(job.jobId, Date.now());
    } catch (err) {
      console.error("Erro ao iniciar exportação");
      toast.error(describeApiError(err, "Falha ao iniciar o processamento do relatório."));
      setExporting(false);
    }
  };

  const pollExportJob = (jobId: string, startedAt: number) => {
    const checkStatus = async () => {
      try {
        const currentJob = await reportsApi.getExportJobStatus(jobId);

        if (currentJob.status === "Completed" && currentJob.downloadUrl) {
          toast.dismiss("export-job-toast");

          // Download direto pela URL pré-assinada do S3. O nome do arquivo vem do
          // Content-Disposition que a API embute na URL -- `download` é ignorado em outra origem.
          const a = document.createElement("a");
          a.href = currentJob.downloadUrl;
          a.download = currentJob.fileName || "relatorio_itens_pedidos.xlsx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          toast.success("Download da planilha iniciado com sucesso!");
          setExporting(false);
          return;
        }

        if (currentJob.status === "Failed") {
          toast.dismiss("export-job-toast");
          toast.error(currentJob.errorMessage || "Falha ao gerar o relatório.");
          setExporting(false);
          return;
        }

        if (Date.now() - startedAt > EXPORT_POLL_TIMEOUT_MS) {
          toast.dismiss("export-job-toast");
          // O job fica registrado no servidor, então continuar a consulta é possível:
          // o relatório provavelmente ainda está sendo gerado.
          toast.error("A geração está demorando mais que o previsto. O relatório continua sendo processado.", {
            duration: 15000,
            action: {
              label: "Continuar aguardando",
              onClick: () => {
                setExporting(true);
                toast.loading("Verificando o relatório novamente...", { id: "export-job-toast" });
                pollExportJob(jobId, Date.now());
              },
            },
          });
          setExporting(false);
          return;
        }

        setTimeout(checkStatus, EXPORT_POLL_INTERVAL_MS);
      } catch (pollErr) {
        toast.dismiss("export-job-toast");
        console.error("Erro ao consultar status da exportação");
        toast.error(describeApiError(pollErr, "Erro ao verificar status do relatório."));
        setExporting(false);
      }
    };

    setTimeout(checkStatus, EXPORT_POLL_INTERVAL_MS);
  };

  const getSituationBadge = (situation: string) => {
    switch (situation) {
      case "Integrado":
        return <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">Integrado</Badge>;
      case "Agendado":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">Agendado</Badge>;
      case "Retido no portal":
        return <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">Retido no portal</Badge>;
      case "Atrasado no envio":
        return <Badge variant="destructive">Atrasado</Badge>;
      case "Cancelado":
        return <Badge variant="outline" className="text-gray-500 border-gray-300">Cancelado</Badge>;
      default:
        return <Badge variant="secondary">{situation || "—"}</Badge>;
    }
  };

  /**
   * Método de pagamento com a origem do dado. Pedido antigo não tem método gravado: a API o deriva
   * e marca em `paymentMethodSource`, então quem lê precisa saber que não é dado registrado.
   */
  const renderPaymentMethod = (item: OrderItemsReportItem) => {
    const source: PaymentMethodSource = item.paymentMethodSource;
    const name = item.paymentMethodName?.trim();

    return (
      <div className="space-y-1">
        <Badge variant="outline" className="font-normal">
          {name || "Não informado"}
        </Badge>
        {source === "derivado" && (
          <div
            className="text-[10px] uppercase tracking-wide text-amber-700"
            title="Pedido anterior ao registro de método: valor inferido do envio, não gravado no pedido."
          >
            inferido
          </div>
        )}
        {source === "indefinido" && (
          <div
            className="text-[10px] uppercase tracking-wide text-muted-foreground"
            title="Não há método de pagamento registrado nem como inferir."
          >
            sem registro
          </div>
        )}
        {item.cardBrandName && (
          <div className="text-xs text-muted-foreground">{item.cardBrandName}</div>
        )}
      </div>
    );
  };

  const items = reportData?.items ?? [];
  const showEmptyState = !isFetching && !isError && items.length === 0;

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-7xl">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-5">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <FileSpreadsheet className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Relatório de Itens de Pedidos</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Visualização e exportação detalhada: as 60 colunas com paridade do Magento mais 4 colunas do portal.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 shadow-sm"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>

          <Button
            onClick={handleExport}
            disabled={exporting || isFetching}
            className="flex items-center gap-2 shadow-sm bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Download className={`h-4 w-4 ${exporting ? "animate-bounce" : ""}`} />
            {exporting ? "Gerando Planilha..." : "Exportar Excel (.xlsx)"}
          </Button>
        </div>
      </div>

      {/* Cards de Métricas / Resumo */}
      {reportData && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="shadow-sm border-l-4 border-l-primary">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">Itens Listados</CardTitle>
              <PackageCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.totalCount.toLocaleString("pt-BR")}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Linhas encontradas no período selecionado
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">Quantidade de Produtos</CardTitle>
              <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reportData.totalQuantity.toLocaleString("pt-BR")} un</div>
              <p className="text-xs text-muted-foreground mt-1">
                Soma de unidades pedidas
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-l-4 border-l-emerald-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total Líquido</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {reportData.totalAmount.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total acumulado dos itens
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Barra de Filtros */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 text-primary" />
              Filtros de Pesquisa
            </CardTitle>
            <CardDescription>
              Defina o período{maxPeriodDays != null ? ` (máx. ${maxPeriodDays} dias)` : ""} e os critérios para refinar os itens do relatório.
            </CardDescription>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            aria-label="Limpar todos os filtros aplicados"
            className="shrink-0"
          >
            <X className="mr-2 h-4 w-4" aria-hidden="true" />
            Limpar Filtros
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Seletor de Datas */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Período Obrigatório
              </label>
              <DateRangePicker date={dateRange} setDate={setDateRange} />
              {periodTooLong && (
                <p className="text-xs text-destructive mt-1">
                  {periodDays} dias — máximo {maxPeriodDays}.
                </p>
              )}
            </div>

            {/* CNPJ */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                CNPJ do Cliente
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  aria-label="CNPJ do Cliente"
                  placeholder="CNPJ completo..."
                  value={cnpjInput}
                  onChange={(e) => setCnpjInput(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {/* Ciclo Comercial (ano) */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Ciclo Comercial
              </label>
              <Select value={cycleYearFilter} onValueChange={setCycleYearFilter}>
                <SelectTrigger aria-label="Ciclo Comercial">
                  <SelectValue placeholder="Todos os Ciclos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os Ciclos</SelectItem>
                  {cycleYearOptions.map((year) => (
                    <SelectItem key={year} value={String(year)}>
                      Ciclo {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo de Pedido do ciclo */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Tipo de Pedido
              </label>
              <Select value={cycleTypeFilter} onValueChange={setCycleTypeFilter}>
                <SelectTrigger aria-label="Tipo de Pedido">
                  <SelectValue placeholder="Todos os Tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os Tipos</SelectItem>
                  {CYCLE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status do Pedido */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Status do Pedido
              </label>
              <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                <SelectTrigger aria-label="Status do Pedido">
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os Status</SelectItem>
                  {(filterOptions?.orderStatuses ?? []).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Situação de Envio */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Situação de Envio
              </label>
              <Select value={situationFilter} onValueChange={setSituationFilter}>
                <SelectTrigger aria-label="Situação de Envio">
                  <SelectValue placeholder="Todas as Situações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas as Situações</SelectItem>
                  {(filterOptions?.shippingSituations ?? []).map((situation) => (
                    <SelectItem key={situation} value={situation}>
                      {situation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Método de Pagamento */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Método de Pagamento
              </label>
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger aria-label="Método de Pagamento">
                  <SelectValue placeholder="Todos os Métodos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os Métodos</SelectItem>
                  {(filterOptions?.paymentMethods ?? []).map((method) => (
                    <SelectItem key={method.code} value={method.code}>
                      {method.name}
                      {!method.isActive && " (histórico)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bandeira do Cartão */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Bandeira do Cartão
              </label>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger aria-label="Bandeira do Cartão">
                  <SelectValue placeholder="Todas as Bandeiras" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas as Bandeiras</SelectItem>
                  {(filterOptions?.cardBrands ?? []).map((brand) => (
                    <SelectItem key={brand.code} value={brand.code}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Dados */}
      <Card className="shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[140px] whitespace-nowrap">Nº Pedido</TableHead>
                <TableHead className="min-w-[260px]">Cliente / Razão Social</TableHead>
                <TableHead className="min-w-[240px]">Produto</TableHead>
                <TableHead className="w-[150px] whitespace-nowrap">Data Pedido</TableHead>
                <TableHead className="w-[170px] whitespace-nowrap">Ciclo / Tipo de Pedido</TableHead>
                <TableHead className="w-[140px] whitespace-nowrap">Status do Pedido</TableHead>
                <TableHead className="w-[190px] whitespace-nowrap">Status da Integração</TableHead>
                <TableHead className="w-[160px] whitespace-nowrap">Situação de Envio</TableHead>
                <TableHead className="w-[180px] whitespace-nowrap">Pagamento</TableHead>
                <TableHead className="w-[130px] whitespace-nowrap">Condição Comercial</TableHead>
                <TableHead className="text-right w-[140px] whitespace-nowrap">Total Líquido</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-10">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                      <span>Carregando itens do relatório...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-12">
                    <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                      <AlertTriangle className="h-8 w-8 text-destructive/70" />
                      <span className="font-medium text-destructive">
                        {describeApiError(error, "Erro ao carregar os dados do relatório.")}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        Tentar de novo
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <TableRow key={item.itemId} className="hover:bg-muted/40 transition-colors">
                    <TableCell className="font-semibold whitespace-nowrap">{item.orderNumber}</TableCell>
                    <TableCell>
                      <div className="font-medium text-sm truncate max-w-[320px] xl:max-w-none" title={item.clientCompanyName}>
                        {item.clientCompanyName}
                      </div>
                      <div className="text-xs text-muted-foreground">{item.clientCnpj || "-"}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm line-clamp-2 max-w-[300px]" title={item.productDescription || undefined}>
                        {item.productDescription || <span className="text-muted-foreground">Sem descrição</span>}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{item.productSku || "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {item.orderCreatedAtLocal
                        ? format(new Date(item.orderCreatedAtLocal), "dd/MM/yyyy HH:mm", { locale: ptBR })
                        : format(new Date(item.orderCreatedAtUtc), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {item.cycleCode ? (
                        <>
                          <div className="font-medium">{resolveCycleYearFromCode(item.cycleCode) ?? "—"}</div>
                          <div className="text-muted-foreground">
                            {describeOrderType(item.cycleCode, item.orderNumber) ?? `Tipo ${item.cycleCode % 100}`}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Sem ciclo</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <StateBadge status={resolveDisplayOrderStatus(item.orderStatus)} />
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {item.integrationStatus ? (
                        <span className="font-mono text-[11px] text-muted-foreground">{item.integrationStatus}</span>
                      ) : (
                        <span className="text-muted-foreground">Sem histórico</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{getSituationBadge(item.shippingSituation)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{renderPaymentMethod(item)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                      {item.paymentCondition || "Não informada"}
                    </TableCell>
                    <TableCell className="text-right font-bold text-sm text-emerald-700 whitespace-nowrap">
                      {item.itemTotalNet.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </TableCell>
                  </TableRow>
                ))
              ) : showEmptyState ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-12">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 text-muted-foreground/60" />
                      <span className="font-medium">Nenhum item encontrado para os filtros selecionados.</span>
                      <span className="text-xs">Tente alterar o período ou os parâmetros de busca.</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>

        {/* Rodapé com Paginação */}
        {reportData && reportData.totalCount > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t bg-muted/20">
            <div className="text-xs text-muted-foreground">
              Mostrando página <strong>{reportData.pageNumber}</strong> de <strong>{reportData.totalPages}</strong> (Total: {reportData.totalCount.toLocaleString("pt-BR")} itens)
            </div>

            <div className="flex items-center gap-2">
              <Select
                value={pageSize.toString()}
                onValueChange={(val) => {
                  setPageSize(parseInt(val, 10));
                  setPageNumber(1);
                }}
              >
                <SelectTrigger aria-label="Itens por página" className="h-8 w-[110px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} por página
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={reportData.pageNumber <= 1 || isFetching}
                className="h-8 text-xs"
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(reportData.totalPages, p + 1))}
                disabled={reportData.pageNumber >= reportData.totalPages || isFetching}
                className="h-8 text-xs"
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
